import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/** Applied to the source task item's `li` for the duration of a drag (see TaskListEditor.scss). */
export const TASK_ITEM_DRAG_SOURCE_CLASS = 'task-item-drag-source';

export const taskItemDragSourcePluginKey = new PluginKey<DecorationSet>('taskItemDragSource');

type TaskItemDragSourceMeta = { from: number; nodeSize: number } | 'clear';

/**
 * Tracks the task item currently being dragged out of the list, marking it with a
 * node decoration so it renders faded/monochrome for the duration of the drag.
 *
 * A decoration (view-only state) rather than a node attr or class mutation: it must
 * not dirty the document, or the drag would trigger a content save.
 */
export const createTaskItemDragSourcePlugin = (): Plugin<DecorationSet> =>
    new Plugin<DecorationSet>({
        key: taskItemDragSourcePluginKey,
        state: {
            init: () => DecorationSet.empty,
            apply(tr, decorations) {
                const meta: TaskItemDragSourceMeta | undefined = tr.getMeta(taskItemDragSourcePluginKey);

                if (meta === 'clear') return DecorationSet.empty;

                if (meta) {
                    return DecorationSet.create(tr.doc, [
                        Decoration.node(meta.from, meta.from + meta.nodeSize, {
                            class: TASK_ITEM_DRAG_SOURCE_CLASS,
                        }),
                    ]);
                }

                return decorations.map(tr.mapping, tr.doc);
            },
        },
        props: {
            decorations(state) {
                return this.getState(state);
            },
        },
    });

/**
 * Marks the task item at `pos` as the active drag's source.
 */
export const setTaskItemDragSource = (editor: Editor, pos: number, node: ProseMirrorNode): void => {
    editor.view.dispatch(
        editor.state.tr.setMeta(taskItemDragSourcePluginKey, { from: pos, nodeSize: node.nodeSize }),
    );
};

/**
 * Restores the source task item's normal appearance (drag ended or cancelled).
 */
export const clearTaskItemDragSource = (editor: Editor): void => {
    if (editor.isDestroyed) return;

    editor.view.dispatch(editor.state.tr.setMeta(taskItemDragSourcePluginKey, 'clear'));
};
